import { Formik, useFormikContext } from 'formik'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import Styles from './BrandPlanAwarenessRedesign.module.scss'
import { Button } from 'blaze-ui/Button'
import { BrandPlan, BrandPlanTactic } from 'types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { generatePath, useHistory } from 'react-router-dom'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { Topics } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Topics'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import { useExperiment } from 'utils/experiments'
import { useCompleteVMOnboarding } from 'hooks/queries/workspaces/useCompleteVMOnboarding'
import { reportError } from 'utils/error'
import { useCreateOnboardingCampaign } from 'hooks/queries/adCampaigns/useCreateOnboardingCampaign'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { Heart, VideoOn, Email, Pencil02 } from 'blaze-ui/icons/24'
import { ArrowRight } from 'blaze-ui/icons/20'
import cx from 'classnames'

interface BrandPlanAwarenessRedesignProps {
  id: number
  selectedBlogPlatform?: string
  selectedEmailPlatform?: string
  isStarterPlan: boolean
  paidAdsOnboardingParameters?: {
    weeklyAdsBudget?: number
    selectedPlatforms: any[]
  }
  onBack: () => void
  onProvideBackHandler?: (handler: (() => void) | null) => void
  onNext: (payload?: any) => void
}

interface FormValues {
  social_topics: string[]
  short_form_video_topics: string[]
  seo_topics: string[]
  email_topics: string[]
}

const SOCIAL_MEDIA_TACTICS: BrandPlanTactic[] = [
  BrandPlanTactic.Instagram,
  BrandPlanTactic.Facebook,
  BrandPlanTactic.LinkedIn,
  BrandPlanTactic.Twitter,
  BrandPlanTactic.GoogleBusinessProfile,
]

const VIDEO_TACTICS: BrandPlanTactic[] = [
  BrandPlanTactic.InstagramReel,
  BrandPlanTactic.TikTok,
  BrandPlanTactic.YouTube,
]

type TabKey = 'social' | 'video' | 'blog' | 'email'

interface TabConfig {
  key: TabKey
  label: string
  name: string
  title: string
  icon: React.ReactNode
}

const TAB_CONFIG: TabConfig[] = [
  { key: 'social', label: 'Social Media', name: 'social_topics', title: 'Social Media', icon: <Heart size={24} /> },
  {
    key: 'video',
    label: 'Video',
    name: 'short_form_video_topics',
    title: 'Short-form Video',
    icon: <VideoOn size={24} />,
  },
  { key: 'blog', label: 'Blog', name: 'seo_topics', title: 'Blog', icon: <Pencil02 size={24} /> },
  { key: 'email', label: 'Email', name: 'email_topics', title: 'Email', icon: <Email size={24} /> },
]

const BrandPlanAwarenessRedesignForm = ({
  brandPlan,
  isStarterPlan,
  paidAdsOnboardingParameters,
  onBack,
  onProvideBackHandler,
  onNext,
}: {
  brandPlan: BrandPlan
  isStarterPlan: boolean
  paidAdsOnboardingParameters?: any
  onBack: () => void
  onProvideBackHandler?: (handler: (() => void) | null) => void
  onNext: (payload?: any) => void
}) => {
  const { values, isSubmitting, handleSubmit } = useFormikContext<FormValues>()
  const { isMobile } = useResponsiveDesign()
  const [activeTab, setActiveTab] = useState<TabKey | null>(null)

  const sectionRefs = useRef<Record<TabKey, HTMLDivElement | null>>({
    social: null,
    video: null,
    blog: null,
    email: null,
  })

  useEffect(() => {
    if (onProvideBackHandler && isMobile) {
      onProvideBackHandler(onBack)
    }
  }, [onBack, onProvideBackHandler, isMobile])

  const hasSocialTopics = useMemo(() => {
    const hasSocialTactics = brandPlan.tactics.some((tactic) => SOCIAL_MEDIA_TACTICS.includes(tactic))
    return hasSocialTactics && values.social_topics.length > 0
  }, [brandPlan.tactics, values.social_topics])

  const hasVideoTopics = useMemo(() => {
    const hasVideoTactics = brandPlan.tactics.some((tactic) => VIDEO_TACTICS.includes(tactic))
    return hasVideoTactics && values.short_form_video_topics.length > 0
  }, [brandPlan.tactics, values.short_form_video_topics])

  const hasBlogTopics = useMemo(() => {
    return brandPlan.tactics.includes(BrandPlanTactic.SEO) && values.seo_topics.length > 0
  }, [brandPlan.tactics, values.seo_topics])

  const hasEmailTopics = useMemo(() => {
    return brandPlan.tactics.includes(BrandPlanTactic.Email) && values.email_topics.length > 0
  }, [brandPlan.tactics, values.email_topics])

  const hasAnyTopics = useMemo(() => {
    return (
      values.social_topics.length > 0 ||
      values.short_form_video_topics.length > 0 ||
      values.seo_topics.length > 0 ||
      values.email_topics.length > 0
    )
  }, [values])

  const availableTabs = useMemo(() => {
    return TAB_CONFIG.filter((tab) => {
      if (tab.key === 'social') return hasSocialTopics
      if (tab.key === 'video') return hasVideoTopics
      if (tab.key === 'blog') return hasBlogTopics
      if (tab.key === 'email') return hasEmailTopics
      return false
    })
  }, [hasSocialTopics, hasVideoTopics, hasBlogTopics, hasEmailTopics])

  useEffect(() => {
    if (isMobile && availableTabs.length > 0 && activeTab === null) {
      setActiveTab(availableTabs[0].key)
    }
  }, [isMobile, availableTabs, activeTab])

  const handleTabClick = useCallback((tabKey: TabKey) => {
    setActiveTab(tabKey)
    const sectionEl = sectionRefs.current[tabKey]
    if (sectionEl) {
      sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return (
    <form onSubmit={handleSubmit}>
      <MultiStepStepLayout
        title={isMobile ? "Last step! Here's your first week of topics" : 'Review topics for your first week'}
        description={
          isMobile
            ? 'Review them, then generate to see your content calendar come to life.'
            : 'Make sure these topics reflect your business goals and themes. Then, press generate and watch your calendar magically fill up!'
        }
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <ResponsiveDesign.Desktop>
                  <Button
                    id="brand-plan-awareness-redesign-back"
                    variant="tertiary"
                    size="lg"
                    onPress={onBack}
                    aria-label="Back"
                  >
                    Back
                  </Button>
                </ResponsiveDesign.Desktop>
                <Button
                  id="brand-plan-awareness-redesign-next"
                  size="lg"
                  type="submit"
                  isDisabled={isSubmitting || !hasAnyTopics}
                  fullWidth={isMobile}
                  endIcon={isMobile ? ArrowRight : undefined}
                >
                  {isMobile ? 'Generate Content' : 'Generate my first week of content'}
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={cx(Styles.container, { [Styles.mobileWrapper]: isMobile })}>
            {isMobile && availableTabs.length > 1 && (
              <div className={Styles.tabBar}>
                {availableTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={cx(Styles.tab, { [Styles.tabActive]: activeTab === tab.key })}
                    onClick={() => handleTabClick(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {hasSocialTopics && (
              <div ref={(el) => (sectionRefs.current.social = el)}>
                <Topics name="social_topics" title="Social Media" icon={<Heart size={24} />} />
              </div>
            )}

            {hasVideoTopics && (
              <div ref={(el) => (sectionRefs.current.video = el)}>
                <Topics name="short_form_video_topics" title="Short-form Video" icon={<VideoOn size={24} />} />
              </div>
            )}

            {hasBlogTopics && (
              <div ref={(el) => (sectionRefs.current.blog = el)}>
                <Topics name="seo_topics" title="Blog" icon={<Pencil02 size={24} />} />
              </div>
            )}

            {hasEmailTopics && (
              <div ref={(el) => (sectionRefs.current.email = el)}>
                <Topics name="email_topics" title="Email" icon={<Email size={24} />} />
              </div>
            )}
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    </form>
  )
}

export const BrandPlanAwarenessRedesign = (props: BrandPlanAwarenessRedesignProps) => {
  const { id, isStarterPlan, paidAdsOnboardingParameters, onBack, onProvideBackHandler, onNext } = props

  const { isIOSNative } = useResponsiveDesign()
  const mobileCalendarAccessVariant = useExperiment('mobile_calendar_access')
  const history = useHistory()
  const { mutateAsync: onboard } = useOnboard()
  const { mutateAsync: startBrandPlan } = useStartBrandPlan({ brandPlanId: id })
  const { trackGenerateContentClicked, trackTopicsReviewed } = useOnboardingTracking()
  const { mutateAsync: markVirtualMarketerOnboardingCompleted } = useCompleteVMOnboarding()
  const { mutate: createOnboardingCampaign } = useCreateOnboardingCampaign()

  const { data: brandPlan } = useBrandPlan(id, { cacheTime: 60 * 1000 * 5 })

  const initialValues = useMemo(() => {
    if (!brandPlan) {
      return {
        social_topics: [],
        short_form_video_topics: [],
        seo_topics: [],
        email_topics: [],
      }
    }

    const socialMediaTopics = [
      ...(brandPlan.crosspost_topics ?? []),
      ...(brandPlan.facebook_topics ?? []),
      ...(brandPlan.instagram_topics ?? []),
      ...(brandPlan.linkedin_topics ?? []),
      ...(brandPlan.twitter_topics ?? []),
    ]

    const videoTopics = [
      ...(brandPlan.video_crosspost_topics ?? []),
      ...(brandPlan.tiktok_topics ?? []),
      ...(brandPlan.youtube_topics ?? []),
    ]

    return {
      social_topics: socialMediaTopics,
      short_form_video_topics: videoTopics,
      seo_topics: brandPlan.seo_topics ?? [],
      email_topics: brandPlan.email_topics ?? [],
    }
  }, [brandPlan])

  const handleSubmit = useCallback(
    async ({ social_topics, short_form_video_topics, seo_topics, email_topics }: FormValues) => {
      if (!brandPlan) return

      try {
        trackTopicsReviewed()
        trackGenerateContentClicked()

        if (paidAdsOnboardingParameters?.selectedPlatforms?.length) {
          createOnboardingCampaign({ weeklyBudget: paidAdsOnboardingParameters.weeklyAdsBudget ?? 0 })
        }

        const { brand_plan: updatedBrandPlan, first_schedule: schedule } = await startBrandPlan({})
        await onboard()
        await markVirtualMarketerOnboardingCompleted()

        const pathname = generatePath(SCHEDULED_POSTS_PATH, {
          workspaceId: updatedBrandPlan.workspace_id,
        })

        if (schedule?.id) {
          const search = `?date=${dayjs(schedule.start_date).format('YYYY-MM-DD')}&load-tutorial=true`

          if (isIOSNative && mobileCalendarAccessVariant === 'allow') {
            sendMessageToNativeApp(
              JSON.stringify({
                type: 'ONBOARDING_COMPLETE',
                data: { pathname, search },
              }),
            )
            onNext({ desktopPath: pathname + search, publishDay: schedule.start_date })
          } else {
            history.push(pathname + search)
          }
        } else {
          if (isIOSNative && mobileCalendarAccessVariant === 'allow') {
            sendMessageToNativeApp(
              JSON.stringify({
                type: 'ONBOARDING_COMPLETE',
                data: { pathname },
              }),
            )
            onNext({ desktopPath: pathname })
          } else {
            history.push(pathname)
          }
        }
      } catch (error) {
        reportError(error)
        showErrorMessageToast('Failed to save topics. Please try again.')
      }
    },
    [
      brandPlan,
      trackTopicsReviewed,
      trackGenerateContentClicked,
      paidAdsOnboardingParameters,
      createOnboardingCampaign,
      startBrandPlan,
      onboard,
      markVirtualMarketerOnboardingCompleted,
      history,
      isIOSNative,
      mobileCalendarAccessVariant,
      onNext,
    ],
  )

  if (!brandPlan) return null

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize>
      <BrandPlanAwarenessRedesignForm
        brandPlan={brandPlan}
        isStarterPlan={isStarterPlan}
        paidAdsOnboardingParameters={paidAdsOnboardingParameters}
        onBack={onBack}
        onProvideBackHandler={onProvideBackHandler}
        onNext={onNext}
      />
    </Formik>
  )
}
